import { useState } from 'react';
import { ChevronRight, ChevronLeft, Target, MessageSquare, Compass, Heart, Briefcase, Dumbbell, Wallet, Brain, Users } from 'lucide-react';
import { supabase } from '../services/supabase';
import { logger } from '../services/logger';

interface CoachingOnboardingProps {
  userName: string;
  onComplete: () => void;
  onSkip?: () => void;
}

const FOCUS_AREAS = [
  { id: 'career', label: 'Career & Work', description: 'Job, skills, professional growth', icon: Briefcase },
  { id: 'health', label: 'Health & Fitness', description: 'Exercise, nutrition, energy', icon: Dumbbell },
  { id: 'habits', label: 'Habits & Discipline', description: 'Routines, breaking bad habits', icon: Target },
  { id: 'relationships', label: 'Relationships', description: 'Social skills, connections', icon: Users },
  { id: 'personal_growth', label: 'Personal Growth', description: 'Mindset, confidence, purpose', icon: Brain },
  { id: 'finance', label: 'Money & Finance', description: 'Budgeting, saving, investing', icon: Wallet }
];

const COACHING_STYLES = [
  {
    id: 'tough_love',
    label: 'Direct & Challenging',
    description: 'No-BS accountability. Push me to do better.',
    icon: Target
  },
  {
    id: 'supportive',
    label: 'Supportive & Encouraging',
    description: 'Patient guidance. Help me build confidence.',
    icon: Heart
  },
  {
    id: 'balanced',
    label: 'Balanced Approach',
    description: 'Mix of challenge and support based on the moment.',
    icon: Compass
  }
];

export function CoachingOnboarding({ userName, onComplete, onSkip }: CoachingOnboardingProps) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [focusArea, setFocusArea] = useState('');
  const [coachingStyle, setCoachingStyle] = useState('balanced');
  const [immediateGoal, setImmediateGoal] = useState('');

  const totalSteps = 3;

  async function handleComplete() {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      await supabase
        .from('user_coaching_preferences')
        .upsert({
          user_id: user.id,
          coaching_style: coachingStyle,
          focus_area: focusArea,
          immediate_goal: immediateGoal.trim() || null,
          updated_at: new Date().toISOString()
        });

      const { data: matchingTopics } = await supabase
        .from('coaching_topics')
        .select('id')
        .eq('is_active', true)
        .or(`category.eq.${focusArea},name.ilike.%${focusArea}%`);

      if (matchingTopics && matchingTopics.length > 0) {
        const topicInserts = matchingTopics.map((topic: { id: string }) => ({
          user_id: user.id,
          coaching_topic_id: topic.id,
          is_enabled: true
        }));

        await supabase
          .from('user_coaching_topics')
          .upsert(topicInserts, { onConflict: 'user_id,coaching_topic_id' });
      }

      await supabase
        .from('profiles')
        .update({
          coaching_onboarding_completed: true,
          welcome_completed: true,
          onboarding_step: 5,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      onComplete();
    } catch (err) {
      logger.error('Error completing onboarding:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleSkip() {
    if (onSkip) {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          await supabase
            .from('profiles')
            .update({
              coaching_onboarding_completed: true,
              welcome_completed: true,
              onboarding_step: 5
            })
            .eq('id', user.id);
        }
      } catch (err) {
        logger.error('Error skipping onboarding:', err);
      }
      onSkip();
    }
  }

  function canProceed(): boolean {
    if (step === 1) return !!focusArea;
    if (step === 2) return !!coachingStyle;
    return true;
  }

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center p-4 sm:p-6">
      <div className="max-w-lg w-full">
        <div className="bg-white rounded-3xl shadow-xl shadow-gray-200/50 overflow-hidden">
          <div className="px-6 pt-6 sm:px-8 sm:pt-8">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-500">Step {step} of {totalSteps}</p>
              {onSkip && (
                <button
                  onClick={handleSkip}
                  className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
                >
                  Skip for now
                </button>
              )}
            </div>

            <div className="flex items-center gap-1.5 mb-6">
              {Array.from({ length: totalSteps }).map((_, i) => (
                <div
                  key={i}
                  className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${
                    i + 1 <= step ? 'bg-coral-500' : 'bg-gray-200'
                  }`}
                />
              ))}
            </div>
          </div>

          <div className="px-6 pb-6 sm:px-8 sm:pb-8">
            {step === 1 && (
              <div className="space-y-5">
                <div>
                  <h2 className="text-2xl font-heading font-bold text-ink-950 mb-1">
                    Hi {userName}, what brings you here?
                  </h2>
                  <p className="text-gray-600">
                    Pick your main focus. You can explore other areas anytime.
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-3">
                  {FOCUS_AREAS.map(area => {
                    const Icon = area.icon;
                    const isSelected = focusArea === area.id;
                    return (
                      <button
                        key={area.id}
                        onClick={() => setFocusArea(area.id)}
                        className={`flex items-center gap-4 p-4 rounded-xl border-2 text-left transition-all ${
                          isSelected
                            ? 'border-coral-500 bg-coral-50'
                            : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          isSelected ? 'bg-coral-500 text-white' : 'bg-gray-100 text-gray-500'
                        }`}>
                          <Icon className="h-5 w-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-ink-950">{area.label}</div>
                          <div className="text-sm text-gray-500">{area.description}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-5">
                <div>
                  <h2 className="text-2xl font-heading font-bold text-ink-950 mb-1">
                    How do you like to be coached?
                  </h2>
                  <p className="text-gray-600">
                    This helps us match your preferred communication style.
                  </p>
                </div>

                <div className="space-y-3">
                  {COACHING_STYLES.map(style => {
                    const Icon = style.icon;
                    const isSelected = coachingStyle === style.id;
                    return (
                      <button
                        key={style.id}
                        onClick={() => setCoachingStyle(style.id)}
                        className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 text-left transition-all ${
                          isSelected
                            ? 'border-coral-500 bg-coral-50'
                            : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          isSelected ? 'bg-coral-500 text-white' : 'bg-gray-100 text-gray-500'
                        }`}>
                          <Icon className="h-5 w-5" />
                        </div>
                        <div className="flex-1">
                          <div className="font-semibold text-ink-950">{style.label}</div>
                          <div className="text-sm text-gray-500">{style.description}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-5">
                <div>
                  <h2 className="text-2xl font-heading font-bold text-ink-950 mb-1">
                    What's one thing you want to accomplish?
                  </h2>
                  <p className="text-gray-600">
                    A goal for the next 30 days. Be as specific as you can — vague goals get vague results.
                  </p>
                </div>

                <div className="space-y-4">
                  <textarea
                    value={immediateGoal}
                    onChange={(e) => setImmediateGoal(e.target.value)}
                    placeholder="e.g., Wake up at 6am every day, Apply to 10 jobs, Go to the gym 3x per week..."
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-coral-400 focus:ring-2 focus:ring-coral-400/20 focus:outline-none transition-all resize-none h-28"
                    autoFocus
                  />

                  {!immediateGoal.trim() && (
                    <div className="bg-amber-50 border border-amber-100 rounded-xl p-4">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <MessageSquare className="h-4 w-4 text-amber-600" />
                        </div>
                        <div className="text-sm text-amber-800">
                          <span className="font-medium">This takes 20 seconds and makes a real difference.</span>{' '}
                          Users who set a goal here are 3x more likely to stay consistent with their coach.
                        </div>
                      </div>
                    </div>
                  )}

                  {immediateGoal.trim() && (
                    <div className="bg-gray-50 rounded-xl p-4">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-coral-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <MessageSquare className="h-4 w-4 text-coral-600" />
                        </div>
                        <div className="text-sm text-gray-600">
                          <span className="font-medium text-ink-950">Your coach will check in</span> on this goal and help you stay accountable.
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="px-6 pb-6 sm:px-8 sm:pb-8 flex items-center gap-3">
            {step > 1 && (
              <button
                onClick={() => setStep(step - 1)}
                className="flex items-center justify-center w-12 h-12 text-gray-500 border border-gray-200 rounded-xl hover:bg-gray-50 hover:text-gray-700 transition-colors"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
            )}

            {step < 3 ? (
              <button
                onClick={() => setStep(step + 1)}
                disabled={!canProceed()}
                className="flex-1 flex items-center justify-center gap-2 py-3.5 px-6 bg-coral-500 text-white font-semibold rounded-xl hover:bg-coral-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-lg shadow-coral-500/25"
              >
                Continue
                <ChevronRight className="h-5 w-5" />
              </button>
            ) : (
              <button
                onClick={handleComplete}
                disabled={loading}
                className="flex-1 flex items-center justify-center gap-2 py-3.5 px-6 bg-coral-500 text-white font-semibold rounded-xl hover:bg-coral-600 disabled:opacity-50 transition-all shadow-lg shadow-coral-500/25"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                    Setting up...
                  </>
                ) : (
                  <>
                    {immediateGoal.trim() ? 'Start Coaching' : 'Skip and start coaching'}
                    <ChevronRight className="h-5 w-5" />
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
